import React from "react";

import { css } from "@emotion/core";
import { useContext, ComponentProps, useState } from "react";

import { CurrencyContext, Price, RoundingStrategy } from "@zapier/currency";

import { Interval, PlanV2 } from "@zapier/checkout/esm/types";
import { Spacing } from "@zapier/checkout/esm/constants";

import { BillingOption } from "./BillingOption";
import { getEnticements } from "./utils/getEnticements";
import AutoUpgradeWarningModal from "../AutoUpgradeWarningModal/AutoUpgradeWarningModal";

type Props = {
  isAutoUpgradeEnrolled?: boolean;
  onSelectOption: (interval: Interval) => void;
  planOptions: PlanV2[];
  selectedOption: Interval;
};

const billingOptionsCss = css`
  display: flex;
  flex-direction: row;

  @supports (gap: ${Spacing.medium}px) {
    gap: ${Spacing.medium}px;
  }

  @supports not (gap: ${Spacing.medium}px) {
    & > :first-of-type {
      margin-right: ${Spacing.medium}px;
    }
  }

  @media (max-width: 750px) {
    flex-direction: column;
  }
`;

function PlanPrice({ plan }: { plan: PlanV2 }) {
  const currency = useContext(CurrencyContext);
  const price = plan.interval === "year" ? plan.amount / 12 : plan.amount;

  const priceProps: ComponentProps<typeof Price> = {
    currency,
    children: price,
  };

  if (currency.zeroDecimal && plan.interval === "year") {
    /** Force rounding up for monthly prices for annual plans */
    priceProps.roundingStrategy = RoundingStrategy.up;
  }

  return <Price {...priceProps} />;
}

export const BillingOptions = ({
  isAutoUpgradeEnrolled = false,
  planOptions,
  onSelectOption,
  selectedOption,
}: Props) => {
  const enticements = getEnticements(planOptions);
  const [showAutoUpgradeWarning, setShowAutoUpgradeWarning] = useState(
    isAutoUpgradeEnrolled && selectedOption === "year"
  );

  const handleSelectOption = (interval: Interval) => {
    if (isAutoUpgradeEnrolled && interval === "year") {
      setShowAutoUpgradeWarning(true);
    } else {
      setShowAutoUpgradeWarning(false);
      onSelectOption(interval);
    }
  };

  const billingOptions = planOptions.map(plan => {
    const intervalName = plan.interval === "year" ? "annual" : plan.interval;
    const name = `pay ${intervalName}ly`;

    // for the free plan monthly is the the only option, so always select it:
    const isSelected =
      planOptions.length === 1 || selectedOption === plan.interval;

    return (
      <BillingOption
        enticement={enticements[plan.interval]}
        isMetered={plan.hasMeteredBilling}
        isOnly={planOptions.length === 1}
        isSelected={isSelected}
        key={name}
        name={name}
        onClick={() => handleSelectOption(plan.interval)}
        price={<PlanPrice plan={plan} />}
      />
    );
  });

  return (
    <div
      aria-label="billing interval options"
      css={billingOptionsCss}
      role="radiogroup"
    >
      {showAutoUpgradeWarning && (
        <AutoUpgradeWarningModal
          onClose={() => {
            setShowAutoUpgradeWarning(false);
            onSelectOption("month");
          }}
          onConfirm={() => {
            setShowAutoUpgradeWarning(false);
            onSelectOption("year");
          }}
        />
      )}
      {billingOptions}
    </div>
  );
};
